/*
 * One product's glyph, rendered.
 *
 * The primitives live in `productGlyphs.ts` so the same definition can be drawn here by React
 * and compared against the marketing page's hand-written SVG by a test. This file is only the
 * rendering — keeping it separate is the same split every product page uses, and the reason
 * the product cluster's filter lives in `productNavModel.ts` so a test can reach it.
 */
import { GLYPH_VIEWBOX, glyphFor, type GlyphPrimitive } from './productGlyphs';

/** Stroke weights matching the mark: a `node` is the heavier accent. */
const STROKE = { line: 1.6, node: 0 } as const;

function Primitive({ p }: { p: GlyphPrimitive }) {
  // `node` primitives are filled dots; `line` primitives are stroked outlines. Both take their
  // colour from `currentColor`, so a glyph inherits whatever the surface around it is using
  // rather than carrying a palette of its own.
  const shared = {
    ...p.attrs,
    fill: p.role === 'node' ? 'currentColor' : 'none',
    stroke: p.role === 'node' ? 'none' : 'currentColor',
    strokeWidth: STROKE[p.role] || undefined,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  if (p.tag === 'rect') return <rect {...shared} />;
  if (p.tag === 'circle') return <circle {...shared} />;
  if (p.tag === 'line') return <line {...shared} />;
  return <path {...shared} />;
}

export function ProductGlyph({ product, size = 20 }: { product: string; size?: number }) {
  const primitives = glyphFor(product);
  // A product with no glyph renders nothing rather than a placeholder. An icon standing in for
  // "we have not drawn one" is indistinguishable from one we chose, which is the whole reason
  // this file exists.
  if (primitives === null) return null;
  return (
    <svg
      viewBox={GLYPH_VIEWBOX}
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0 }}
    >
      {primitives.map((p, i) => (
        <Primitive key={i} p={p} />
      ))}
    </svg>
  );
}
